<script setup lang="ts">
import type { MediaSummary, PlaybackRecord } from '../../../shared/contracts'
import { Play, Trash2 } from '@lucide/vue'
import { computed } from 'vue'
import { rememberMediaSummary } from '@/services/mediaCache'
import { detailLocation, playbackLocation } from '@/utils/navigation'
import PosterArt from './PosterArt.vue'

const props = defineProps<{ media: MediaSummary, record?: PlaybackRecord, removable?: boolean, from?: string, showPlaybackStatus?: boolean, directPlayback?: boolean }>()
const emit = defineEmits<{ remove: [] }>()
const mediaLocation = computed(() => props.directPlayback
    ? playbackLocation(props.media, props.record?.episodeIndex, props.from || '/')
    : detailLocation(props.media, props.from || '/'))

function playbackLabel(media: MediaSummary, record: PlaybackRecord) {
    const percent = Math.round(Math.max(0, Math.min(record.progress, 1)) * 100)
    if (media.kind === 'movie' || (media.episodeCount ?? 0) <= 1) { return `看到 ${percent}%` }
    const sourceLabel = record.episodeLabel.split('·').at(-1)?.trim()
    const episodeLabel = sourceLabel && sourceLabel.length <= 12 ? sourceLabel : `第 ${record.episodeIndex + 1} 集`
    return `看到 ${episodeLabel}`
}
</script>

<template>
    <div class="media-card group/card relative min-w-0">
        <RouterLink
            :to="mediaLocation"
            class="group block min-w-0 rounded-[3px] focus-ring"
            @click="rememberMediaSummary(media)"
        >
            <div class="media-card__poster relative aspect-[2/3] overflow-hidden rounded-2xl bg-[var(--c-poster)] shadow-[0_1px_0_var(--c-border)] transition-transform duration-300 ease-out">
                <PosterArt :src="media.poster" :alt="media.title" />
                <span v-if="(showPlaybackStatus && record) || media.remarks" class="absolute inset-x-2 top-2 line-clamp-2 rounded-md px-1.5 py-1 text-center text-[11px] font-600 leading-4 shadow-sm sm:left-auto sm:max-w-[82%] sm:px-2 sm:text-xs" :class="showPlaybackStatus && record ? 'border border-[rgb(var(--accent-rgb)/30%)] bg-[rgb(var(--accent-rgb)/88%)] text-white' : 'border border-white/12 bg-black/58 text-white'">
                    {{ showPlaybackStatus && record ? playbackLabel(media, record) : media.remarks }}
                </span>
                <span v-if="media.score" class="absolute bottom-2 left-2 rounded-[2px] bg-black/64 px-1.5 py-0.5 text-xs text-[#e6b866] font-700">
                    {{ media.score }}
                </span>
                <span class="media-card__play absolute bottom-2 right-2 grid size-10 translate-y-2 place-items-center border border-white/22 rounded-full bg-black/58 text-white opacity-0 shadow-md transition duration-250">
                    <Play class="ml-0.5 size-4 fill-current" />
                </span>
                <div v-if="showPlaybackStatus && record?.progress && record.progress > 0" class="absolute inset-x-0 bottom-0 h-1 bg-white/15">
                    <div class="h-full rounded-r-full bg-[var(--accent)]" :style="{ width: `${Math.min(record.progress * 100, 100)}%` }" />
                </div>
            </div>
        </RouterLink>
        <div class="mt-3 min-w-0">
            <div class="flex items-center gap-1.5">
                <RouterLink :to="mediaLocation" class="min-w-0 flex-1 rounded-sm focus-ring" @click="rememberMediaSummary(media)">
                    <h3 class="truncate text-sm text-[var(--c-text)] font-650 transition sm:text-[15px] group-hover/card:text-[var(--accent)]">
                        {{ media.title }}
                    </h3>
                </RouterLink>
                <button v-if="removable" class="grid size-7 shrink-0 place-items-center rounded-full text-[var(--c-text-faint)] transition hover:bg-[var(--c-fill-hover)] hover:text-[var(--c-danger)] focus-ring" :aria-label="`从观看历史中移除《${media.title}》`" @click="emit('remove')">
                    <Trash2 class="size-3.5" />
                </button>
            </div>
            <div class="mt-1 flex items-center gap-2 truncate text-xs text-[var(--c-text-muted)] uppercase">
                <span>{{ media.year || '年份未知' }}</span>
                <span class="size-0.5 shrink-0 rounded-full bg-[var(--c-text-faint)]" />
                <span class="truncate">{{ media.category || media.sourceName }}</span>
                <span v-if="media.alternatives?.length" class="ml-auto shrink-0 text-[var(--accent)] normal-case">{{ media.alternatives.length + 1 }} 源</span>
            </div>
        </div>
    </div>
</template>

<style scoped>
.media-card {
    content-visibility: auto;
    contain-intrinsic-block-size: auto 320px;
}

@media (hover: hover) and (pointer: fine) {
    .group:hover .media-card__poster {
        transform: translate3d(0, -4px, 0);
    }

    .group:hover .media-card__play {
        opacity: 1;
        transform: translate3d(0, 0, 0);
    }
}
</style>
